/**
 * Product Sticky Add to Cart
 * Shows a sticky bar at the top when user scrolls past the main product form
 */

class ProductStickyATC {
  constructor(scope = document) {
    this.scope = scope;
    this.stickyBar = scope.querySelector('.prodcut-sticky-atc') || document.querySelector('.prodcut-sticky-atc');
    if (!this.stickyBar) return;

    this.productForm = scope.querySelector('product-form');
    this.mainForm = scope.querySelector('form[action*="/cart/add"]') || scope.querySelector('form[id^="AddToCartForm"]');
    this.variantSelectsElement = scope.querySelector('variant-selects');
    this.variantInputs = this.stickyBar.querySelectorAll('.sticky-variant-button input');
    this.variantListItems = this.stickyBar.querySelectorAll('.hide--desktop ul li');
    this.atcButton = this.stickyBar.querySelector('.sticky-atc-btn');
    this.atcButtonMobile = this.stickyBar.querySelector('.sticky-atc-trigger-mobile');
    this.priceElement = this.stickyBar.querySelector('.sticky-price-regular');
    this.comparePriceElement = this.stickyBar.querySelector('.sticky-price-compare');
    this.priceElementMobile = this.stickyBar.querySelector('.hide--desktop .sticky-price-regular');
    this.comparePriceElementMobile = this.stickyBar.querySelector('.hide--desktop .sticky-price-compare');
    this.priceObserver = null;

    // Handle variant selection for desktop
    this.variantInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleVariantChange(e));
    });

    // Handle variant selection for mobile
    this.variantListItems.forEach(item => {
      item.addEventListener('click', (e) => this.handleMobileVariantChange(e));
    });

    this.init();
  }

  init() {
    // Show/hide sticky on scroll
    this.handleScroll();
    window.addEventListener('scroll', () => this.handleScroll());

    // Handle ATC click
    if (this.atcButton) {
      this.atcButton.addEventListener('click', () => this.handleATCClick());
    }

    if (this.atcButtonMobile) {
      this.atcButtonMobile.addEventListener('click', () => this.handleATCClickMobile());
    }

    // Subscribe to variant changes from main form using pub/sub system
    if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.optionValueSelectionChange) {
      this.variantChangeUnsubscriber = window.subscribe(
        window.theme.PUB_SUB_EVENTS.optionValueSelectionChange,
        (data) => {
          setTimeout(() => {
            this.handleMainFormVariantChange(data);
          }, 2000);
        }
      );
    }

    // Observe price changes in the main form
    this.observePriceChanges();

    this.syncPreorderState();
  }

  handleScroll() {
    const targetElement = this.mainForm || this.productForm || this.variantSelectsElement;
    if (!targetElement) return;

    const formRect = targetElement.getBoundingClientRect();
    const formBottom = formRect.bottom;

    if (formBottom < 0) {
      this.stickyBar.classList.add('visible');
    } else {
      this.stickyBar.classList.remove('visible');
    }
  }

  handleVariantChange(e) {
    const input = e.currentTarget;
    if (input.disabled) return;
    const allVariantButtons = this.stickyBar.querySelectorAll('.sticky-variant-button');
    allVariantButtons.forEach(button => button.classList.remove('is-active'));

    const parentLabel = input.closest('.sticky-variant-button');
    if (parentLabel) {
      parentLabel.classList.add('is-active');
    }

    // Update price
    const price = input.dataset.price;
    if (this.priceElement && price) {
      this.priceElement.textContent = price;
    }

    // Trigger variant change on main form using variant ID
    const variantId = input.dataset.variantId;
    this.selectMainFormVariant(variantId);
  }

  handleMobileVariantChange(e) {
    const listItem = e.currentTarget;

    // Don't process if disabled
    if (listItem.classList.contains('disabled')) return;

    const variantTitle = listItem.getAttribute('data-value');

    // Find the matching variant in the product
    const container = this.variantSelectsElement || this.productForm;
    if (!container) return;

    // Find the variant by title in the main form
    const variantInputs = container.querySelectorAll('input[type="radio"][name*="option"]');

    variantInputs.forEach(input => {
      const inputValue = input.value;
      if (inputValue === variantTitle) {
        input.checked = true;

        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Close the mobile variant selector
    if (this.atcButtonMobile) {
      this.atcButtonMobile.classList.remove('active');
    }

    // Wait for DOM to update, then add to cart automatically
    setTimeout(() => {
      this.handleATCClick();
    }, 2000);
  }

  handleATCClick() {
    // Simply trigger the main form submit - variant validation is handled by the main form
    const container = this.mainForm || this.productForm;
    const mainATCButton = container?.querySelector('[name="add"]');
    if (mainATCButton) {
      mainATCButton.click();
    }
  }

  handleATCClickMobile() {
    const hasVariants = this.atcButtonMobile.dataset.variant === 'true';
    const isSoldout = this.atcButtonMobile.classList.contains('soldout');

    // If soldout, do nothing
    if (isSoldout) {
      return;
    }

    // If product has multiple variants, toggle the variant selector
    if (hasVariants) {
      this.atcButtonMobile.classList.toggle('active');
      return;
    }

    const container = this.mainForm || this.productForm;
    const mainATCButton = container?.querySelector('[name="add"]');
    if (mainATCButton) {
      setTimeout(() => {
        mainATCButton.click();
      }, 200);
    }
  }

  selectMainFormVariant(variantId) {
    if (!(this.mainForm || this.productForm) || !variantId) return;

    window.__stickyBarVariantChange = true;

    if (this._stickyFlagTimeout) clearTimeout(this._stickyFlagTimeout);
    this._stickyFlagTimeout = setTimeout(() => {
      window.__stickyBarVariantChange = false;
    }, 1000);

    const formContainer = this.mainForm || this.productForm;

    const variantSelect = formContainer.querySelector('select[name="id"]') || formContainer.querySelector('input[name="id"]');
    if (variantSelect) {
      if (variantSelect.tagName === 'SELECT' || (variantSelect.tagName === 'INPUT' && variantSelect.type === 'hidden')) {
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Method 2: Use robust JSON data to map ID -> Option Values
    // Try to find product data in the page
    let productScript = document.querySelector(`script[data-product-id]`);
    if (!productScript) {
      // Alternative: look for the product data in window object
      productScript = document.querySelector(`script[type="application/json"][data-product-json]`);
    }

    if (productScript) {
      try {
        const productData = JSON.parse(productScript.textContent);
        const variant = productData.variants.find(v => v.id == variantId);

        if (variant && variant.options) {
          const currentVariantSelects = this.scope.querySelector('variant-selects');
          const currentProductForm = this.scope.querySelector('product-form');
          const inputContainer = currentVariantSelects || currentProductForm || this.variantSelectsElement || this.productForm;

          const selectedOptionValueIds = [];
          let firstInput = null;

          variant.options.forEach((val, i) => {
            const optionIndex = "option" + (i + 1);
            const safeValue = val.replace(/"/g, '\\"');

            // Try finding the input by value and index
            let input = inputContainer.querySelector(`input[data-index="${optionIndex}"][value="${safeValue}"]`);

            // Fallback for custom selectors (like popouts) using hidden inputs
            if (!input) {
              const hiddenInput = inputContainer.querySelector(`input[type="hidden"][data-index="${optionIndex}"]`);
              if (hiddenInput) {
                hiddenInput.value = val;
                input = hiddenInput;

                // Update UI for popout if present
                const popout = hiddenInput.closest('popout-select');
                if (popout) {
                  const triggerText = popout.querySelector('[data-popout-text]');
                  if (triggerText) triggerText.textContent = val;
                  popout.querySelectorAll('.select-popout__item').forEach(li => {
                    li.classList.toggle('select-popout__item--current', li.getAttribute('data-value') === val);
                  });
                }
              }
            }

            // Final fallback: try finding by just value if index attribute is missing or different
            if (!input) {
              input = inputContainer.querySelector(`input[value="${safeValue}"]`);
            }

            if (input) {
              console.log('[ProductStickyATC] Found input for option', optionIndex, 'with value', val, input);
              if (input.type === 'radio' || input.type === 'checkbox') {
                input.checked = true;
                input.dispatchEvent(new Event('change', { bubbles: true }));
              }

              // Helper for visual active state on labels
              const label = inputContainer.querySelector(`label[for="${input.id}"]`);
              if (label) {
                // Remove active class from siblings
                const name = input.name;
                inputContainer.querySelectorAll(`input[name="${name}"]`).forEach(sibling => {
                  const sibLabel = inputContainer.querySelector(`label[for="${sibling.id}"]`);
                  if (sibLabel) sibLabel.classList.remove('is-active', 'selected');
                });
                label.classList.add('is-active', 'selected');
              }

              input.dispatchEvent(new Event('change', { bubbles: true }));

              // Collect ID for manual PubSub
              if (input.dataset.optionValueId) {
                selectedOptionValueIds.push(input.dataset.optionValueId);
              }
              if (!firstInput) firstInput = input;

            } else {
              console.warn('[ProductStickyATC] Could not find input for option', optionIndex, 'with value', val, 'in container', inputContainer);
            }
          });

          // Manually dispatch PubSub event to ensure ProductInfo updates
          // This ensures compatibility even if 'change' event doesn't bubble correctly to VariantSelects
          if (window.theme && window.theme.PUB_SUB_EVENTS && window.theme.PUB_SUB_EVENTS.optionValueSelectionChange && window.publish && selectedOptionValueIds.length > 0) {
            window.publish(window.theme.PUB_SUB_EVENTS.optionValueSelectionChange, {
              data: {
                event: new Event('change'), // Mock event
                target: firstInput || inputContainer,
                selectedOptionValues: selectedOptionValueIds
              }
            });
          }

          return;
        }
      } catch (e) {
        console.warn("[ProductStickyATC] Error parsing product JSON", e);
      }
    }

    // Method 3: Fallback to text matching (Legacy/Simple products)
    // If still no product data or failed to parse, try to get it from the sticky bar's data
    const stickyInput = this.stickyBar.querySelector(`input[data-variant-id="${variantId}"]`);
    if (stickyInput) {
      // Get the option value from the sticky input's label or data
      const parentLabel = stickyInput.closest('.sticky-variant-button');
      if (parentLabel) {
        const optionText = parentLabel.textContent.trim();

        // Find the matching input in the main form by option value
        const inputContainer = this.variantSelectsElement || this.productForm;
        if (inputContainer) {
          const optionInputs = inputContainer.querySelectorAll('input[data-single-option-selector]');
          optionInputs.forEach(input => {
            if (input.value === optionText) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        }
      }
    }
  }

  handleMainFormVariantChange(data) {
    // Get the current variant ID from the main form
    const formContainer = this.mainForm || this.productForm;
    if (!formContainer) return;

    const variantIdInput = formContainer.querySelector('[name="id"]');
    if (!variantIdInput) return;

    const variantId = variantIdInput.value;

    setTimeout(() => {
      this.updateStickyVariant(variantId);
    }, 2000);
  }

  updateStickyVariant(variantId) {

    // Remove all active states first
    const allVariantButtons = this.stickyBar.querySelectorAll('.sticky-variant-button');
    allVariantButtons.forEach(button => button.classList.remove('is-active'));

    // Find and activate the matching variant
    let matchingInput = null;
    this.variantInputs.forEach(input => {
      if (input.dataset.variantId === String(variantId)) {
        matchingInput = input;
        input.checked = true;

        // Add active state to parent label
        const parentLabel = input.closest('.sticky-variant-button');
        if (parentLabel) {
          parentLabel.classList.add('is-active');
        }
      }
    });

    // Update prices from main form
    this.updatePricesFromMainForm();

    // Sync Preorder state
    this.syncPreorderState();
  }

  syncPreorderState() {
    const formContainer = this.mainForm || this.productForm;
    const mainATCButton = formContainer?.querySelector('[name="add"]');
    if (!mainATCButton) return;

    const isPreorder = mainATCButton.classList.contains('PreOrder-Btn');

    // Get text from main button EXCLUDING price element
    // The main button structure is often <span data-atc-text> Text <span price>...</span></span>
    const sourceTextSpan = mainATCButton.querySelector('[data-add-to-cart-text]');
    let sourceText = "";

    if (sourceTextSpan) {
      sourceTextSpan.childNodes.forEach(node => {
        // Get only text nodes and ignore price spans
        if (node.nodeType === Node.TEXT_NODE) {
          sourceText += node.textContent;
        }
      });
    } else {
      // Fallback: try to strip price if we can identify it
      const priceClone = mainATCButton.cloneNode(true);
      const price = priceClone.querySelector('[data-product-price], .price, .money');
      if (price) price.remove();
      sourceText = priceClone.textContent;
    }

    sourceText = sourceText.trim();

    const buttons = [this.atcButton, this.atcButtonMobile].filter(Boolean);

    buttons.forEach(btn => {
      // Sync Class
      if (isPreorder) btn.classList.add('PreOrder-Btn');
      else btn.classList.remove('PreOrder-Btn');

 
      if (btn.classList.contains('sticky-atc-trigger-mobile')) {
        return;
      }

      const targetTextContainer = btn.querySelector('.btn-text') || btn.querySelector('.add-text') || btn;

      let textUpdated = false;
      targetTextContainer.childNodes.forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0) {
          node.textContent = sourceText + ' ';
          textUpdated = true;
        }
      });

      if (!textUpdated) {
        if (targetTextContainer.firstChild) {
          const textNode = document.createTextNode(sourceText + ' ');
          targetTextContainer.insertBefore(textNode, targetTextContainer.firstChild);
        } else {
          targetTextContainer.textContent = sourceText; // If empty, just set it
        }
      }
    });

    // Also sync the hidden property input if we can find it in the main form, 
    // just in case JS needs to toggle it for validity (though liquid usually handles the initial state)
    const preorderInput = this.mainForm?.querySelector('.preorder-property') || this.productForm?.querySelector('.preorder-property');
    if (preorderInput) {
      if (isPreorder) {
        preorderInput.removeAttribute('disabled');
      } else {
        preorderInput.setAttribute('disabled', 'disabled');
      }
    }
  }

  observePriceChanges() {
    const initObserver = () => {
      const priceContainer = document.querySelector('[data-add-to-cart-text]');

      if (!priceContainer) {
        return false;
      }


      this.priceObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            const rechargePrice = priceContainer.querySelector('.recharge-inner-most-price');
            const regularPrice = priceContainer.querySelector('.product__price--regular.pdp-product');

            if (rechargePrice || regularPrice) {
              const priceText = rechargePrice ? rechargePrice.textContent.trim() : regularPrice.textContent.trim();
              this.updatePricesFromMainForm();
            }
          }
        });
      });

      this.priceObserver.observe(priceContainer, {
        childList: true,
        characterData: true,
        subtree: true
      });

      return true;
    };

    // Try to initialize observer multiple times to account for Recharge loading delay
    let attempts = 0;
    const maxAttempts = 10;
    const retryInterval = 500;

    const tryInit = () => {
      attempts++;

      if (initObserver()) {
        return;
      }

      if (attempts < maxAttempts) {
        setTimeout(tryInit, retryInterval);
      } else {
        console.log('Failed to initialize observer after maximum attempts');
      }
    };

    // Start trying after initial delay
    setTimeout(tryInit, 1000);
  }

  updatePricesFromMainForm() {
    // Update regular price from main form
    // Try Recharge price first, fallback to regular price
    let mainPrice = document.querySelector('.recharge-inner-most-price');

    if (!mainPrice) {
      // Fallback to regular price if Recharge is not present
      mainPrice = document.querySelector('.product__price--regular.pdp-product');
    }

    if (mainPrice) {
      const priceText = mainPrice.textContent.trim();

      // Update desktop price
      if (this.priceElement) {
        this.priceElement.textContent = priceText;
      }

      // Update mobile price
      if (this.priceElementMobile) {
        this.priceElementMobile.textContent = priceText;
      }

    }

    // Update compare price from main form
    const mainComparePrice = document.querySelector('[data-compare-price]');

    if (mainComparePrice && mainComparePrice.textContent.trim()) {
      const comparePriceText = mainComparePrice.textContent.trim();

      // Update desktop compare price
      if (this.comparePriceElement) {
        this.comparePriceElement.textContent = comparePriceText;
        this.comparePriceElement.style.display = '';
      }

      // Update mobile compare price
      if (this.comparePriceElementMobile) {
        this.comparePriceElementMobile.textContent = comparePriceText;
        this.comparePriceElementMobile.style.display = '';
      }

    } else {
      // Hide compare price if not present
      if (this.comparePriceElement) {
        this.comparePriceElement.style.display = 'none';
      }
      if (this.comparePriceElementMobile) {
        this.comparePriceElementMobile.style.display = 'none';
      }
    }
  }
}

// Expose class globally
window.ProductStickyATC = ProductStickyATC;

const initStickyATC = () => {
  const sections = document.querySelectorAll('[id^="productTemplate--"]');
  if (sections.length > 0) {
    sections.forEach(section => {
      // Prevent double initialization
      if (!section.dataset.stickyAtcInitialized) {
        new ProductStickyATC(section);
        section.dataset.stickyAtcInitialized = 'true';
      }
    });
  }
};

if (document.readyState === 'loading') {
  // Use event listener for loading state
  document.addEventListener('DOMContentLoaded', initStickyATC);
} else {
  // Run immediately if already loaded
  initStickyATC();
}

// Fallback: try again after a slight delay to catch late renders/dynamic content
setTimeout(initStickyATC, 500);
setTimeout(initStickyATC, 1500);
